import React, { useState, useEffect, useCallback } from 'react';
import Button from './ui/Button';
import { procurifyLabService } from '../services/procurifyLabService';

const formatDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const formatMoney = (amount, currency) => {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
};

/** Procurify AP bill status: 2 = Approved */
const PROCURIFY_STATUS_APPROVED = 2;

const applyBillStatusFilter = (rows, filterKey) => {
  const list = Array.isArray(rows) ? rows : [];
  if (filterKey === 'pending') {
    return list.filter((b) => b.status !== PROCURIFY_STATUS_APPROVED);
  }
  return list;
};

/**
 * Procurify bills queue for Lab Approvals (primary tab).
 */
const LabApprovalsProcurifyPanel = ({
  user,
  approvalByFolioId,
  linkedWorkorderBadge,
  onOpenBill,
  openingBillId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bills, setBills] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [assigneeEmail, setAssigneeEmail] = useState(() =>
    procurifyLabService.defaultAssigneeEmail()
  );
  /** pending = hide Approved client-side (default); all = show full assignee list */
  const [billStatusFilter, setBillStatusFilter] = useState('pending');

  const loadBills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const email =
        (assigneeEmail || procurifyLabService.defaultAssigneeEmail() || '').trim();
      const body = await procurifyLabService.fetchAssignedBills({
        assigneeEmail: email,
        page: 1,
        pageSize: 50,
      });
      let rows = Array.isArray(body.bills) ? body.bills : [];
      rows = applyBillStatusFilter(rows, billStatusFilter);
      setBills(rows);
      setPagination(body.pagination || null);
    } catch (e) {
      setError(e.message || 'Could not load Procurify bills');
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [assigneeEmail, billStatusFilter]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const handleOpenInProcurify = (e, bill) => {
    e.stopPropagation();
    procurifyLabService.openBillInProcurify(bill.uuid || bill.id);
  };

  return (
    <section className="lab-approvals-docusign" aria-label="Procurify bills awaiting approval">
      <div className="lab-approvals-docusign__head">
        <h2 className="lab-approvals-docusign__title">Awaiting your approval (Procurify)</h2>
        <p className="lab-approvals-docusign__hint">
          Bills assigned to you in Procurify. Click a row to download the invoice PDF and run{' '}
          <strong>AI extract</strong> (same review modal as DocuSign). Use{' '}
          <strong>Approve</strong> or <strong>Deny</strong> in the review modal side panel (Procurify
          API as configured assignee). <strong>Status</strong> filter defaults to hiding{' '}
          <code className="lab-approvals-code">Approved</code> bills (same idea as DocuSign status).
          Saves <code className="lab-approvals-code">folio_id</code> (bill uuid),{' '}
          <code className="lab-approvals-code">procurify_bill_id</code>, and{' '}
          <code className="lab-approvals-code">software_source=procurify</code>.
        </p>
        <div className="lab-approvals-docusign__toolbar">
          <label className="lab-approvals-docusign__filter">
            <span className="lab-approvals-docusign__filter-label">Assignee</span>
            <input
              type="email"
              className="status-filter lab-approvals-docusign__filter-select"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(e.target.value)}
              disabled={loading}
              aria-label="Procurify assignee email"
            />
          </label>
          <label className="lab-approvals-docusign__filter">
            <span className="lab-approvals-docusign__filter-label">Status</span>
            <select
              className="status-filter lab-approvals-docusign__filter-select"
              value={billStatusFilter}
              onChange={(e) => setBillStatusFilter(e.target.value)}
              disabled={loading}
              aria-label="Filter Procurify bills by status"
            >
              <option value="pending">Awaiting action (hide Approved)</option>
              <option value="all">All</option>
            </select>
          </label>
          {!loading && bills.length > 0 ? (
            <span className="lab-approvals-docusign__filter-count">
              {bills.length} bill
              {bills.length === 1 ? '' : 's'}
            </span>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={loadBills}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>
      {loading && <p className="lab-approvals-docusign__status">Loading Procurify…</p>}
      {error && (
        <p className="lab-approvals-docusign__error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && bills.length === 0 && (
        <p className="lab-approvals-docusign__empty">No bills assigned to this approver.</p>
      )}
      {bills.length > 0 && (
        <ul className="lab-approvals-docusign__list">
          {bills.map((bill) => {
            const billKey = bill.uuid || bill.id;
            const norm = bill.uuid ? String(bill.uuid).trim().toLowerCase() : '';
            const linkedWo = norm && approvalByFolioId ? approvalByFolioId.get(norm) : null;
            const linkBadge = linkedWorkorderBadge ? linkedWorkorderBadge(linkedWo) : null;
            const opening =
              openingBillId != null &&
              (String(openingBillId) === String(bill.id) ||
                String(openingBillId) === String(bill.uuid));
            return (
              <li key={billKey}>
                <button
                  type="button"
                  className="lab-approvals-docusign__row"
                  disabled={!billKey || opening}
                  onClick={() => onOpenBill(bill)}
                  title="Download invoice PDF and run AI extract"
                >
                  <span className="lab-approvals-docusign__subj">
                    Invoice {bill.invoice_number || '—'}
                    {bill.group != null ? (
                      <span className="lab-approvals-docusign__seg-count">
                        Shipment {bill.group}
                      </span>
                    ) : null}
                  </span>
                  <span className="lab-approvals-docusign__wo-list" aria-hidden="true">
                    <span className="lab-approvals-docusign__wo-chip">{bill.vendor_name || '—'}</span>
                  </span>
                  <span className="lab-approvals-docusign__meta">
                    <span className="lab-approvals-docusign__meta-primary">
                      {bill.status_label || bill.status || '—'}
                      <span className="lab-approvals-docusign__eid">
                        Due {formatDate(bill.due_date)} · Submitted {formatDate(bill.submitted_date)}
                      </span>
                    </span>
                    <span className="lab-approvals-docusign__meta-primary">
                      {formatMoney(bill.total_cost_with_tax, bill.currency_code)}
                    </span>
                    {linkBadge ? (
                      <span
                        className={`lab-approvals-docusign__link-badge lab-approvals-docusign__link-badge--${linkBadge.key}`}
                        title={linkBadge.title}
                      >
                        {linkBadge.wid ? `${linkBadge.wid} · ` : ''}
                        {linkBadge.label}
                      </span>
                    ) : null}
                  </span>
                </button>
                <button
                  type="button"
                  className="lab-approvals-procurify-open-link"
                  onClick={(e) => handleOpenInProcurify(e, bill)}
                >
                  Open in Procurify
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {user?.email ? (
        <p className="lab-approvals-docusign__hint lab-approvals-procurify-signed-in">
          Signed in as {user.name || user.email} (Microsoft).
        </p>
      ) : null}
    </section>
  );
};

export default LabApprovalsProcurifyPanel;
